/* ================================================================
   HighlightsPanel — Sidebar panel listing all saved highlights,
   grouped by session/query context. Supports filtering, export,
   clear all, and jump-to-message.
   ================================================================ */

import { useCallback, useMemo, useState } from 'react';
import type { Highlight, HighlightColor } from '../highlights';
import {
  loadHighlights,
  saveHighlights,
  clearAllHighlights,
  exportHighlightsMarkdown,
  HIGHLIGHT_COLORS,
} from '../highlights';

interface Props {
  /** Called when user clicks a highlight to jump to that message */
  onJumpToMessage: (messageId: string) => void;
  onClose: () => void;
  /** Refresh key — bumped when highlights change externally */
  refreshKey: number;
  /** Called after highlights are modified */
  onHighlightsChanged: () => void;
  /** When true, hides own header/close button (parent provides them) */
  embedded?: boolean;
}

/* ── Colour dot ────────────────────────────────────────────── */

function ColorDot({ color }: { color: HighlightColor }) {
  return (
    <span
      className="hl-panel-dot"
      style={{ background: HIGHLIGHT_COLORS[color]?.border || HIGHLIGHT_COLORS.yellow.border }}
      aria-hidden="true"
    />
  );
}

/* ── Panel ────────────────────────────────────────────────── */

export default function HighlightsPanel({ onJumpToMessage, onClose, refreshKey, onHighlightsChanged, embedded }: Props) {
  const [highlights, setHighlights] = useState<Highlight[]>(() => loadHighlights());
  const [filter, setFilter] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  // Refresh when refreshKey changes
  useMemo(() => {
    setHighlights(loadHighlights());
  }, [refreshKey]);

  /* ── Filtered list ── */
  const filtered = useMemo(() => {
    if (!filter.trim()) return highlights;
    const q = filter.toLowerCase();
    return highlights.filter(
      (h) =>
        h.text.toLowerCase().includes(q) ||
        h.note.toLowerCase().includes(q) ||
        (h.sessionContext || '').toLowerCase().includes(q),
    );
  }, [highlights, filter]);

  /* ── Group by session context ── */
  const grouped = useMemo(() => {
    const groups = new Map<string, Highlight[]>();
    for (const h of filtered) {
      const key = h.sessionContext || '(Uncategorised)';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(h);
    }
    return groups;
  }, [filtered]);

  /* ── Remove a single highlight ── */
  const handleRemove = useCallback(
    (id: string) => {
      const updated = highlights.filter((h) => h.id !== id);
      saveHighlights(updated);
      setHighlights(updated);
      onHighlightsChanged();
    },
    [highlights, onHighlightsChanged],
  );

  /* ── Clear all ── */
  const handleClearAll = useCallback(() => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    clearAllHighlights();
    setHighlights([]);
    setConfirmClear(false);
    onHighlightsChanged();
  }, [confirmClear, onHighlightsChanged]);

  /* ── Export ── */
  const handleExport = useCallback(() => {
    const md = exportHighlightsMarkdown(highlights);
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-hub-highlights-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [highlights]);

  return (
    <div className="highlights-panel">
      {!embedded && (
        <div className="highlights-header">
          <h3 className="highlights-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 20h9" />
              <path d="m16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838.838-2.872a2 2 0 0 1 .506-.854z" />
            </svg>
            Highlights ({highlights.length})
          </h3>
          <button className="highlights-close" onClick={onClose} aria-label="Close highlights">
            ✕
          </button>
        </div>
      )}

      {/* Actions bar */}
      {highlights.length > 0 && (
        <div className="highlights-actions">
          <button
            className="highlights-action-btn"
            onClick={handleExport}
            title="Export highlights as markdown"
          >
            Export
          </button>
          <button
            className={`highlights-action-btn highlights-action-btn--danger${confirmClear ? ' highlights-action-btn--confirm' : ''}`}
            onClick={handleClearAll}
            title={confirmClear ? 'Click again to confirm' : 'Clear all highlights'}
          >
            {confirmClear ? 'Confirm clear?' : 'Clear all'}
          </button>
        </div>
      )}

      {/* Filter */}
      {highlights.length > 3 && (
        <div className="highlights-filter">
          <input
            type="text"
            placeholder="Filter highlights…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="highlights-filter-input"
            aria-label="Filter highlights"
          />
        </div>
      )}

      {/* List */}
      <div className="highlights-list">
        {highlights.length === 0 && (
          <div className="highlights-empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true" style={{ opacity: 0.4 }}>
              <path d="M12 20h9" />
              <path d="m16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838.838-2.872a2 2 0 0 1 .506-.854z" />
            </svg>
            <p>No highlights yet</p>
            <p className="highlights-empty-hint">
              Select text in any response and click "Highlight" or "Add Note" to annotate it.
            </p>
          </div>
        )}

        {highlights.length > 0 && filtered.length === 0 && (
          <div className="highlights-empty">
            <p>No matching highlights</p>
          </div>
        )}

        {Array.from(grouped.entries()).map(([ctx, items]) => (
          <div key={ctx} className="highlights-group">
            <div className="highlights-group-header">{ctx}</div>
            {items.map((h) => (
              <div
                key={h.id}
                className="highlight-card"
                onClick={() => onJumpToMessage(h.messageId)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onJumpToMessage(h.messageId);
                }}
              >
                <div className="highlight-card-text">
                  <ColorDot color={h.color} />
                  <span className="highlight-card-excerpt">"{h.text}"</span>
                </div>
                {h.note && (
                  <div className="highlight-card-note">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    {h.note}
                  </div>
                )}
                <div className="highlight-card-meta">
                  <span className="highlight-card-date">
                    {new Date(h.created_at).toLocaleDateString()}
                  </span>
                </div>
                <button
                  className="highlight-card-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(h.id);
                  }}
                  aria-label="Remove highlight"
                  title="Remove"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
